/**
 * DataTable marker expander — FRONTEND PREVIEW port.
 *
 * The email builder (`parse-email.ts:parseDataTable`) serialises a DataTable's
 * configuration into one opaque marker:
 *
 *   <div class="datatable-block">{{dataTable:<base64url-json config>}}</div>
 *
 * At REAL send the consumer (`main-consumer/src/lib/data-table-render.ts`)
 * decodes the marker, joins the matching guest/appointment rows from the
 * payload and emits an Outlook-safe `<table>`. The marker NEVER reaches the
 * inbox. The PSendEmail / test-mail HTML preview, however, renders the stored
 * builder HTML directly — so without this expander the raw `{{dataTable:…}}`
 * token shows up verbatim in the preview (and the surrounding block reads as
 * empty chrome).
 *
 * This is the preview twin of that consumer renderer: it decodes each marker
 * and renders the SAME styled table. Real rows aren't available client-side, so
 * data-bound sources (companions / appointments) stand in three believable
 * SAMPLE rows — identical to what the live builder canvas shows
 * (`A4Builder/children/DataTable.tsx`, shared via `data-table-sample.ts`).
 * Custom-source tables carry their static rows inline in the marker, so those
 * render real content.
 *
 * Per-column display conditions are evaluated against the preview recipient,
 * mirroring the consumer's `applyColumnConditions` so the preview matches the
 * inbox. Fail-safe: an undecodable marker collapses to '' (same as an
 * empty/unknown table at send) rather than leaking the raw token.
 *
 * DESKTOP-LAYOUT NOTE (critical): parse-email wraps the marker in
 * `<div class="datatable-block">` and injects a `@media (max-width:620px)`
 * rule (parseBody) that collapses `.datatable-block` tables into stacked
 * one-cell-per-line cards (thead hidden, the column name before each value) on narrow
 * clients. The PSendEmail preview iframe is a fixed 600px viewport, so that
 * breakpoint would ALWAYS fire there and the preview would show the MOBILE
 * stack instead of the desktop table the admin designed in the canvas. So the
 * preview re-wraps the rendered table under a DIFFERENT class
 * (`datatable-block-preview`) that the mobile rule does not select — the
 * preview pane is a desktop-width frame and must mirror the canvas + the
 * desktop inbox. The real sent email is unaffected: the consumer still emits
 * `datatable-block`, so true mobile clients still get the responsive stack.
 *
 * KEEP ALIGNED with main-consumer/src/lib/data-table-render.ts (renderer) and
 * main-frontend/src/components/A4Builder/data-table-catalog.ts (column types).
 */
import {
  CUSTOM_COLUMN_KEY,
  findColumn,
  type ColumnType,
  type DataTableSource,
} from "@/components/A4Builder/data-table-catalog";
import {
  SAMPLE_ROW_COUNT,
  sampleText,
  sampleColor,
  sampleImageGradient,
} from "@/components/A4Builder/data-table-sample";
import {
  DEFAULT_INCLUDED_STATUSES,
  humanizeAppointmentStatus,
} from "@/components/A4Builder/appointment-record";
import { humanizeAcceptanceState } from "@/components/A4Builder/companion-record";
import { ConditionGroup } from "@/types/EventRule";
import { evaluateConditionGroup } from "./evaluate-condition";

// Single combined matcher, applied in ONE String.replace pass (never re-scans
// the inserted table HTML — important: a custom cell could legitimately contain
// the literal text `{{dataTable:..}}`). Alternation is tried left-to-right, so
// the wrapped branch wins when present:
//   1. the WHOLE `<div class="datatable-block">{{dataTable:..}}</div>` wrapper
//      (group 1) — parseDataTable's exact output, tolerant of surrounding
//      whitespace — so the preview can re-wrap under a class the mobile-stack
//      @media rule doesn't target (see the DESKTOP-LAYOUT NOTE above), OR
//   2. a BARE/unwrapped marker (group 2) — defensive; shouldn't occur in
//      builder HTML, but never leak the raw token if the wrapper ever drifts.
const MARKER =
  /<div class="datatable-block">\s*\{\{dataTable:([A-Za-z0-9_-]+)\}\}\s*<\/div>|\{\{dataTable:([A-Za-z0-9_-]+)\}\}/g;

// Preview-only wrapper class. Deliberately NOT `datatable-block` so the
// `@media (max-width:620px) .datatable-block …` mobile-stack rules (which fire
// in the 600px preview iframe) don't collapse the desktop table.
const PREVIEW_WRAPPER_CLASS = "datatable-block-preview";

// Appointment status filtering + sort are applied at send time on the real
// joined rows; the preview's illustrative SAMPLE rows have nothing to filter,
// so neither is re-applied here.
const DEFAULT_MAX_ROWS = 50;
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

const MIN_COL_CHARS = 6;
const MAX_COL_CHARS = 24;

type DTColumn = {
  key: string;
  label: string;
  value?: string;
  condition?: ConditionGroup;
};

type DataTableConfig = {
  v: 1;
  source: DataTableSource;
  columns: DTColumn[];
  rows?: string[][];
  style: {
    headerBg: string;
    headerColor: string;
    bodyBg: string;
    bodyColor: string;
    borderColor: string;
    borderWidth: number;
    padding: number;
    /** Optional per-side cell padding ("Innenabstand gesondert"); absent sides
     *  fall back to `padding`. See cellPaddingCss below. */
    paddingTop?: number;
    paddingRight?: number;
    paddingBottom?: number;
    paddingLeft?: number;
    fontType?: string;
    striped: boolean;
  };
  filter?: { statuses?: string[]; acceptanceStates?: string[] };
  sort?: "startDtAsc" | "startDtDesc";
  maxRows?: number;
  emptyText?: string;
};

/**
 * Real per-recipient rows for a data-bound source, keyed by the catalog `key`.
 * When the preview has fetched the recipient's actual data (currently
 * companions — see PSendEmail/Preview), it passes the projected records here so
 * the table renders the REAL rows the recipient will receive instead of the
 * illustrative SAMPLE rows. A `undefined` entry means "not available — fall back
 * to sample"; a defined array (even empty) means "this is the real set" and an
 * empty one collapses the table exactly like a real send.
 */
export type RealTableRecords = {
  companions?: Record<string, string>[];
  appointments?: Record<string, string>[];
};

/**
 * Replace every `{{dataTable:…}}` marker in `html` with a rendered preview
 * table. `guest` is the preview recipient (used to evaluate per-column display
 * conditions); with `null` a column's condition is evaluated as for a guest
 * who has no values — an "is empty" condition keeps its column, an "equals"
 * one drops it. `realRecords` optionally
 * supplies the recipient's REAL data-bound rows (see {@link RealTableRecords});
 * sources without a real set stand in believable SAMPLE rows.
 */
export function expandDataTableMarkers(
  html: string,
  guest: Record<string, unknown> | null | undefined,
  realRecords?: RealTableRecords,
  /** Render language for appointment status humanization (raw enum → localized),
   *  mirroring the consumer renderer. Falls back to English when omitted. */
  lang?: string | null,
): string {
  if (!html || !html.includes("{{dataTable:")) return html;
  const render = (b64: string): string => {
    const cfg = decodeConfig(b64);
    // Decode failure → '' (whole block disappears), same as send.
    if (!cfg) return "";
    const table = renderPreviewTable(cfg, guest ?? null, realRecords, lang);
    // Empty table (no rows, no emptyText) → '' so the block disappears exactly
    // like the real send, rather than leaving a stray empty wrapper div.
    return table ? `<div class="${PREVIEW_WRAPPER_CLASS}">${table}</div>` : "";
  };
  MARKER.lastIndex = 0;
  return html.replace(MARKER, (_full, wrappedB64?: string, bareB64?: string) =>
    render(wrappedB64 ?? bareB64 ?? ""),
  );
}

/**
 * Neutralise the `datatable-block` mobile-stack for ALREADY-RENDERED email HTML
 * (no marker left — e.g. the backend's stored sent-email `htmlContent` shown in
 * PEmailSent). parse-email's `@media (max-width:620px) .datatable-block …` rule
 * fires in any fixed ≤620px preview iframe and collapses the table to mobile
 * cards. Renaming just the wrapper div's class (the injected <style> still
 * targets `.datatable-block`, which no longer matches) makes the preview show
 * the desktop table — what a desktop recipient actually saw. Idempotent; a
 * no-op when the class isn't present.
 */
export function neutralizeDataTableMobileStack(html: string): string {
  if (!html || !html.includes('class="datatable-block"')) return html;
  return html.replace(/class="datatable-block"/g, `class="${PREVIEW_WRAPPER_CLASS}"`);
}

/**
 * The inverse of {@link neutralizeDataTableMobileStack}: give an expanded
 * preview table its real `datatable-block` wrapper back, so the mail's own
 * narrow-screen rules (the stacked cards with their labels) apply exactly as
 * in the sent mail. The builder's mail preview uses it for both of its
 * devices — a desktop frame is never narrower than the mail's breakpoint, so
 * the rules only ever fire in the phone frame. Idempotent.
 */
export function restoreDataTableMobileStack(html: string): string {
  if (!html || !html.includes(`class="${PREVIEW_WRAPPER_CLASS}"`)) return html;
  return html.split(`class="${PREVIEW_WRAPPER_CLASS}"`).join('class="datatable-block"');
}

/**
 * True when `html` contains at least one `{{dataTable:…}}` marker whose decoded
 * config has the given source. Lets the preview fetch a data-bound source's real
 * rows (e.g. appointments) only when a table actually uses it — no needless
 * round-trip for an email that has only a companions/custom table.
 */
export function htmlHasDataTableSource(
  html: string | null | undefined,
  source: DataTableSource,
): boolean {
  if (!html || !html.includes("{{dataTable:")) return false;
  MARKER.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MARKER.exec(html)) !== null) {
    const b64 = m[1] ?? m[2];
    if (!b64) continue;
    const cfg = decodeConfig(b64);
    if (cfg && cfg.source === source) {
      MARKER.lastIndex = 0;
      return true;
    }
  }
  return false;
}

/**
 * The `style.fontType` of every decodable `{{dataTable:…}}` marker in `html`.
 *
 * A table's font is the one font value that is NOT visible as CSS in stored
 * builder html — it lives inside the marker until the consumer expands it at
 * send time. Anything that inspects the stored html for fonts (see
 * `src/lib/fonts/fontHtmlStale.ts`) has to look in here too.
 */
export function dataTableMarkerFontTypes(
  html: string | null | undefined,
): string[] {
  if (!html || !html.includes("{{dataTable:")) return [];
  const fonts: string[] = [];
  MARKER.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MARKER.exec(html)) !== null) {
    const b64 = m[1] ?? m[2];
    if (!b64) continue;
    const font: unknown = decodeConfig(b64)?.style?.fontType;
    if (typeof font === "string" && font) fonts.push(font);
  }
  MARKER.lastIndex = 0;
  return fonts;
}

/**
 * Whether any decodable `{{dataTable:…}}` marker in `html` carries NO font.
 * Such a table arrives in the client's default face (Times in Outlook): the
 * expanders write `font-family` only from the marker, and no body font reaches
 * a table. The export writes the template's font into every marker since plan
 * fluffy-strolling-sonnet P1.9; a stored marker without one is worth one
 * regenerating save (fontHtmlStale.ts).
 */
export function dataTableMarkersMissingFont(html: string | null | undefined): boolean {
  if (!html || !html.includes("{{dataTable:")) return false;
  MARKER.lastIndex = 0;
  let m: RegExpExecArray | null;
  let missing = false;
  while (!missing && (m = MARKER.exec(html)) !== null) {
    const b64 = m[1] ?? m[2];
    if (!b64) continue;
    const cfg = decodeConfig(b64);
    if (!cfg) continue;
    const font: unknown = cfg.style?.fontType;
    if (typeof font !== "string" || !font) missing = true;
  }
  MARKER.lastIndex = 0;
  return missing;
}

function decodeConfig(b64: string): DataTableConfig | null {
  try {
    const normalised = b64.replace(/-/g, "+").replace(/_/g, "/");
    const pad = normalised.length % 4;
    const padded = pad === 0 ? normalised : normalised + "=".repeat(4 - pad);
    const bin = atob(padded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    const raw = JSON.parse(json) as unknown;
    if (raw === null || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    if (r.v !== 1) return null;
    if (
      r.source !== "companions" &&
      r.source !== "appointments" &&
      r.source !== "sessions" &&
      r.source !== "custom"
    ) {
      return null;
    }
    if (!Array.isArray(r.columns)) return null;
    if (r.style === null || typeof r.style !== "object") return null;
    return raw as DataTableConfig;
  } catch {
    return null;
  }
}

/** Drop any column whose display condition the preview recipient doesn't match
 *  (custom rows are positional, so survivors are re-projected). Mirrors the
 *  consumer's applyColumnConditions. */
function applyColumnConditions(
  cfg: DataTableConfig,
  guest: Record<string, unknown> | null,
): DataTableConfig {
  const cols = cfg.columns ?? [];
  if (!cols.some((c) => c.condition)) return cfg;
  const kept: number[] = [];
  const columns = cols.filter((c, i) => {
    const keep = !c.condition || evaluateConditionGroup(c.condition, guest);
    if (keep) kept.push(i);
    return keep;
  });
  if (cfg.rows == null) return { ...cfg, columns };
  const rows = cfg.rows.map((row) =>
    kept.map((i) => (Array.isArray(row) ? row[i] : undefined) ?? ""),
  );
  return { ...cfg, columns, rows };
}

function previewColumnType(source: DataTableSource, col: DTColumn): ColumnType {
  // Custom-value columns and the custom source are always plain text.
  if (col.value !== undefined || col.key === CUSTOM_COLUMN_KEY) return "text";
  return findColumn(source, col.key)?.type ?? "text";
}

function renderPreviewTable(
  rawCfg: DataTableConfig,
  guest: Record<string, unknown> | null,
  realRecords?: RealTableRecords,
  lang?: string | null,
): string {
  const cfg = applyColumnConditions(rawCfg, guest);
  if (cfg.columns.length === 0) {
    return cfg.emptyText ? emptyTable(cfg) : "";
  }

  // Real per-recipient rows for this data-bound source, if the preview fetched
  // them (companions / appointments). `undefined` → fall back to SAMPLE; a
  // defined array (even empty) → render the real set, an empty one collapsing
  // the table exactly like the real send.
  const real =
    cfg.source === "companions"
      ? realRecords?.companions
      : cfg.source === "appointments"
        ? realRecords?.appointments
        : undefined;

  // Build the row records the table renders. Custom source uses the inline
  // rows; data-bound sources render the recipient's REAL rows when available,
  // else stand in believable SAMPLE rows — exactly what the builder canvas
  // previews. `usingSample` drives whether image/color cells paint a real
  // value or a believable swatch (sample rows have no live URL/hex).
  let records: Record<string, string>[];
  let usingSample = false;
  if (cfg.source === "custom") {
    const inline = Array.isArray(cfg.rows) ? cfg.rows : [];
    const max = cfg.maxRows ?? DEFAULT_MAX_ROWS;
    const limited = inline.length > max ? inline.slice(0, max) : inline;
    records = limited.map((row) => {
      const rec: Record<string, string> = {};
      cfg.columns.forEach((c, i) => {
        rec[c.key || `c${i}`] = (Array.isArray(row) ? row[i] : "") ?? "";
      });
      return rec;
    });
  } else if (cfg.source === "appointments" && real !== undefined) {
    // Real appointments: apply the SAME status filter → sort → truncate →
    // humanize the send-time renderer applies (the FE rows arrive ascending by
    // startDt, so startDtDesc is a reverse). Filter on the RAW status the
    // records carry, BEFORE the table reads only the configured columns.
    records = prepareAppointmentRecords(cfg, real, lang);
  } else if (cfg.source === "companions" && real !== undefined) {
    // Real companions: apply the SAME acceptance-state filter → truncate →
    // humanize the send-time renderer applies, so the preview matches the
    // sent table. Filter reads the RAW acceptanceState the records carry.
    records = prepareCompanionRecords(cfg, real, lang);
  } else if (real !== undefined) {
    // Other real data-bound rows: keep only the configured columns' keys,
    // honour maxRows, and default any missing cell to '' (same projection the
    // send-time renderer applies to the joined payload rows).
    const max = cfg.maxRows ?? DEFAULT_MAX_ROWS;
    const limited = real.length > max ? real.slice(0, max) : real;
    records = limited.map((src) => {
      const rec: Record<string, string> = {};
      cfg.columns.forEach((c) => {
        rec[c.key] = src[c.key] ?? "";
      });
      return rec;
    });
  } else {
    usingSample = true;
    records = Array.from({ length: SAMPLE_ROW_COUNT }, (_, rowIndex) => {
      const rec: Record<string, string> = {};
      cfg.columns.forEach((c) => {
        if (c.value !== undefined) {
          rec[c.key] = c.value;
        } else if (c.key === CUSTOM_COLUMN_KEY) {
          rec[c.key] = "";
        } else {
          // Localized where the real send-time value is locale-formatted
          // (dates/times/statuses/booleans) — matches the consumer render.
          rec[c.key] = sampleText(c.key, rowIndex, lang);
        }
      });
      return rec;
    });
  }

  if (records.length === 0) {
    return cfg.emptyText ? emptyTable(cfg) : "";
  }
  return fullTable(cfg, records, cfg.source, usingSample);
}

/**
 * Status-filter → sort → truncate → humanize the recipient's REAL appointment
 * rows, mirroring main-consumer/src/lib/data-table-render.ts. Filtering reads
 * the RAW `appointmentStatus` each record carries (present even when the table
 * doesn't display that column); humanization runs AFTER filtering so the raw
 * enum is available for the match. Records are cloned so the cached real set is
 * never mutated.
 */
function prepareAppointmentRecords(
  cfg: DataTableConfig,
  real: ReadonlyArray<Record<string, string>>,
  lang?: string | null,
): Record<string, string>[] {
  // `cfg.filter?.statuses ?? DEFAULT` — an explicit empty array means "none"
  // (collapses the table), exactly like the consumer's nullish-coalescing.
  const allowed = cfg.filter?.statuses
    ? new Set(cfg.filter.statuses)
    : DEFAULT_INCLUDED_STATUSES;
  let rows = real.filter((r) => {
    const s = r["appointmentStatus"];
    return typeof s === "string" && allowed.has(s);
  });
  if (cfg.sort === "startDtDesc") rows = [...rows].reverse();
  const max = cfg.maxRows ?? DEFAULT_MAX_ROWS;
  if (rows.length > max) rows = rows.slice(0, max);
  return rows.map((r) => ({
    ...r,
    appointmentStatus: humanizeAppointmentStatus(r["appointmentStatus"], lang),
  }));
}

/**
 * Acceptance-filter → truncate → humanize the recipient's REAL companion rows,
 * mirroring main-consumer/src/lib/data-table-render.ts. Unlike appointments,
 * an ABSENT `acceptanceStates` means "show all" (legacy templates never had the
 * filter), so only a defined list narrows. Humanization runs AFTER filtering so
 * the raw enum (SYSTEM distinct from ACCEPTED) is available for the match.
 */
function prepareCompanionRecords(
  cfg: DataTableConfig,
  real: ReadonlyArray<Record<string, string>>,
  lang?: string | null,
): Record<string, string>[] {
  let rows = Array.isArray(cfg.filter?.acceptanceStates)
    ? real.filter((r) => {
        const s = r["acceptanceState"];
        return typeof s === "string" && cfg.filter!.acceptanceStates!.includes(s);
      })
    : [...real];
  const max = cfg.maxRows ?? DEFAULT_MAX_ROWS;
  if (rows.length > max) rows = rows.slice(0, max);
  return rows.map((r) => ({
    ...r,
    acceptanceState: r["acceptanceState"]
      ? humanizeAcceptanceState(r["acceptanceState"], lang)
      : "",
  }));
}

function computeColWidthPercents(
  cfg: DataTableConfig,
  rows: ReadonlyArray<Record<string, string>>,
): number[] {
  const n = cfg.columns.length;
  if (n === 0) return [];
  const weights = cfg.columns.map((c, j) => {
    const type = previewColumnType(cfg.source, c);
    if (type === "image") return 14;
    if (type === "color") return 6;
    let maxLen = c.label.length;
    if (c.value !== undefined) {
      maxLen = Math.max(maxLen, c.value.length);
    } else {
      const key = c.key || `c${j}`;
      for (const row of rows) {
        const v = row[key];
        if (v != null) maxLen = Math.max(maxLen, String(v).length);
      }
    }
    return Math.max(MIN_COL_CHARS, Math.min(MAX_COL_CHARS, maxLen));
  });
  const total = weights.reduce((a, b) => a + b, 0);
  const pct: number[] = [];
  let acc = 0;
  for (let i = 0; i < n - 1; i++) {
    const p = Math.max(1, Math.round(((weights[i] ?? 0) * 100) / total));
    pct.push(p);
    acc += p;
  }
  pct.push(Math.max(1, 100 - acc));
  return pct;
}

/**
 * Cell padding CSS value: `8px` (uniform) or `4px 8px 12px 16px` (per-side).
 * Per-side kicks in when ANY side field is a number; absent sides fall back to
 * the uniform `padding` (default 8). KEEP ALIGNED byte-for-byte with
 * main-consumer `data-table-render.ts` and main-backend
 * `DataTableMarkerExpander.java` (cross-repo parity fixture
 * `data-table-padding-parity.json`).
 */
function cellPaddingCss(s: DataTableConfig["style"]): string {
  const base = typeof s.padding === "number" ? s.padding : 8;
  const hasSides =
    typeof s.paddingTop === "number" ||
    typeof s.paddingRight === "number" ||
    typeof s.paddingBottom === "number" ||
    typeof s.paddingLeft === "number";
  if (!hasSides) return `${base}px`;
  const top = typeof s.paddingTop === "number" ? s.paddingTop : base;
  const right = typeof s.paddingRight === "number" ? s.paddingRight : base;
  const bottom = typeof s.paddingBottom === "number" ? s.paddingBottom : base;
  const left = typeof s.paddingLeft === "number" ? s.paddingLeft : base;
  return `${top}px ${right}px ${bottom}px ${left}px`;
}

function fullTable(
  cfg: DataTableConfig,
  rows: ReadonlyArray<Record<string, string>>,
  source: DataTableSource,
  usingSample: boolean,
): string {
  const s = cfg.style;
  const cellPadding = cellPaddingCss(s);
  const tableStyle = [
    "border-collapse:collapse",
    "width:100%",
    "table-layout:fixed",
    `background:${escAttr(s.bodyBg)}`,
    `border:${s.borderWidth}px solid ${escAttr(s.borderColor)}`,
    "mso-table-lspace:0pt",
    "mso-table-rspace:0pt",
    s.fontType ? `font-family:${escAttr(s.fontType)}` : "",
  ]
    .filter((x) => x.length > 0)
    .join(";");

  const colWidths = computeColWidthPercents(cfg, rows);
  // Per-column type + alignment (shared header/body). number/currency right-align;
  // everything else keeps left, so text tables stay byte-identical with the
  // cross-repo parity fixture. (In a TEMPLATE preview an AF column's numeric type
  // is unknown — previewColumnType returns "text" — so this only bites a future
  // catalog numeric column; the send renderers do the real numeric alignment.)
  const colTypes: ColumnType[] = cfg.columns.map((c) => previewColumnType(source, c));
  const alignOf = (t: ColumnType): "left" | "right" =>
    t === "number" || t === "currency" ? "right" : "left";
  const headerCells = cfg.columns
    .map(
      (c, i) =>
        `<th style="width:${colWidths[i]}%;padding:${cellPadding};text-align:${alignOf(colTypes[i]!)};word-wrap:break-word;overflow-wrap:break-word;border:${s.borderWidth}px solid ${escAttr(s.borderColor)};">${escapeText(c.label)}</th>`,
    )
    .join("");

  const bodyRows = rows
    .map((row, i) => {
      const rowBg = s.striped && i % 2 === 1 ? mixDarken(s.bodyBg) : s.bodyBg;
      const cells = cfg.columns
        .map((c, j) => {
          const type = colTypes[j]!;
          // SAMPLE data-bound image/color columns have no live URL/hex, so paint
          // the same believable swatch the builder canvas shows. REAL rows
          // (companions / appointments) carry the actual URL/hex and go through
          // renderCell — same as custom and custom-value cells.
          const isDataBoundSample =
            usingSample &&
            source !== "custom" &&
            c.value === undefined &&
            c.key !== CUSTOM_COLUMN_KEY;
          let cellHtml: string;
          if (isDataBoundSample && type !== "text") {
            cellHtml = sampleVisualHtml(type, i);
          } else {
            const value = c.value !== undefined ? c.value : row[c.key || `c${j}`];
            const valueStr = value === null || value === undefined ? "" : value;
            cellHtml = renderCell(valueStr, type);
          }
          const alignCss = alignOf(type) === "right" ? "text-align:right;" : "";
          // The phone card's label: a hidden first child that the mail's
          // head rule (`.datatable-block .vt-dl`) shows on narrow screens.
          // Gmail drops `attr()`, so the `td::before` label never showed
          // there. `data-label` stays for mails stored before that rule.
          // Byte-identical to main-consumer data-table-render.ts — the
          // shared fixture data-table-label-parity.json pins both.
          const label = escAttr(c.label);
          return `<td data-label="${label}" style="padding:${cellPadding};${alignCss}word-wrap:break-word;overflow-wrap:break-word;border:${s.borderWidth}px solid ${escAttr(s.borderColor)};"><span class="vt-dl" style="display:none;mso-hide:all;">${label}: </span>${cellHtml}</td>`;
        })
        .join("");
      return `<tr style="background:${escAttr(rowBg)};color:${escAttr(s.bodyColor)};">${cells}</tr>`;
    })
    .join("");

  return [
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" style="${tableStyle};">`,
    `<thead><tr style="background:${escAttr(s.headerBg)};color:${escAttr(s.headerColor)};">${headerCells}</tr></thead>`,
    `<tbody>${bodyRows}</tbody>`,
    `</table>`,
  ].join("");
}

function emptyTable(cfg: DataTableConfig): string {
  const s = cfg.style;
  const colSpan = Math.max(cfg.columns.length, 1);
  return [
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;table-layout:fixed;background:${escAttr(s.bodyBg)};border:${s.borderWidth}px solid ${escAttr(s.borderColor)};">`,
    `<tr><td colspan="${colSpan}" style="padding:${cellPaddingCss(s)};color:${escAttr(s.bodyColor)};text-align:center;">${escapeText(cfg.emptyText ?? "")}</td></tr>`,
    `</table>`,
  ].join("");
}

/** Believable swatch for a data-bound image/color SAMPLE cell — mirrors the
 *  builder canvas `SampleCell` (gradient thumbnail / colour chip) so the HTML
 *  preview matches the design instead of an empty/broken cell. */
function sampleVisualHtml(type: ColumnType, rowIndex: number): string {
  if (type === "image") {
    return `<span style="display:inline-block;width:40px;height:30px;border-radius:6px;background:${sampleImageGradient(rowIndex)};border:1px solid rgba(0,0,0,0.06);"></span>`;
  }
  if (type === "color") {
    return `<span style="display:inline-block;width:16px;height:16px;border-radius:4px;background:${sampleColor(rowIndex)};border:1px solid rgba(0,0,0,0.08);vertical-align:middle;"></span>`;
  }
  return "";
}

function renderCell(value: string, type: ColumnType): string {
  if (type === "image") {
    return `<img src="${escUrlAttr(value)}" alt="" style="max-width:80px;height:auto;display:inline-block;">`;
  }
  if (type === "color") {
    if (!HEX_COLOR.test(value)) return "";
    return `<span style="display:inline-block;width:16px;height:16px;border-radius:2px;background:${value};vertical-align:middle;"></span>`;
  }
  return escapeText(value);
}

function escapeText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escUrlAttr(value: string): string {
  if (
    !/^https?:\/\//i.test(value) &&
    !/^data:image\//i.test(value) &&
    !/^cid:/i.test(value)
  ) {
    return "";
  }
  return escAttr(value);
}

function mixDarken(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (m === null) return hex;
  const n = parseInt(m[1]!, 16);
  const r = Math.max(0, ((n >> 16) & 0xff) - 12);
  const g = Math.max(0, ((n >> 8) & 0xff) - 12);
  const b = Math.max(0, (n & 0xff) - 12);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
